"use client";

import React from "react";
import { CircleCheck as CheckCircle2, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function CallToAction() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const benefits = ["Independent options across carriers", "Guidance centered on your goals and questions.", "Clear, straightforward answers"];
  return <section id="call-to-action" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="relative overflow-hidden rounded-2xl bg-primary text-primary-foreground border border-primary/20 shadow-xl px-6 py-12 sm:px-12 sm:py-16 text-center">
          {/* Subtle architectural background accents */}
          <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-chart-2/10 blur-3xl" aria-hidden="true" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-chart-2/5 blur-3xl" aria-hidden="true" />

          <div className="relative z-10 mx-auto max-w-3xl flex flex-col items-center">
            {/* Context Badge */}
            <Badge variant="outline" className="border-chart-2/40 bg-chart-2/10 text-primary-foreground font-semibold uppercase tracking-wider text-xs px-3.5 py-1 mb-6 rounded-full">
              <ShieldCheck className="size-3.5 text-chart-2 mr-1" />
              Retirement & Life Protection
            </Badge>

            {/* Display Heading */}
            <h2 className="font-default text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl md:text-5xl leading-tight">
              Plan your retirement with clarity and confidence
            </h2>

            {/* Supporting Copy */}
            <p className="mt-4 text-base sm:text-lg text-primary-foreground/80 leading-relaxed max-w-2xl font-sans">
              Sit down with an independent specialist to review your insurance and annuity options, understand your numbers, and get clear answers to your questions.
            </p>

            {/* Trust Checklist */}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-y-2 gap-x-6 text-sm text-primary-foreground/90 font-medium">
              {benefits.map((item, index) => <div key={item} data-index={index} className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-chart-2 shrink-0" />
                  <span>{item}</span>
                </div>)}
            </div>

            {/* Action Buttons */}
            <div className="mt-10 flex justify-center w-full">
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-primary-foreground/20 text-primary-foreground bg-primary-foreground/5 hover:bg-primary-foreground/10 hover:text-primary-foreground font-medium px-6 py-3.5 transition-[color,background-color,border-color]">
                <Link to="/FAQKnowledgeCenterPage">
                  Explore knowledge center
                </Link>
              </Button>
            </div>

            {/* Reassurance Disclaimer */}
            <p className="mt-6 text-xs text-primary-foreground/60">
              Free educational consultation. Licensed professionals dedicated to objective guidance.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}